import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search, Sparkles, Star, ArrowRight, Check, Trash2, Send, X, ShoppingCart,
  ClipboardList,
} from "lucide-react";
import { serviceIconFor, CATEGORY_ICON } from "@/lib/serviceIcons";

/** Category → icon accent gradient (used for Apple-app-style rounded tiles). */
const CAT_ACCENT = {
  credit_financial:   "from-amber-500 to-orange-600",
  start_business:     "from-emerald-500 to-teal-600",
  online_business:    "from-sky-500 to-indigo-600",
  start_brand:        "from-fuchsia-500 to-rose-600",
  nonprofit:          "from-rose-500 to-red-600",
  websites_tech:      "from-slate-800 to-slate-600",
  marketing_growth:   "from-violet-500 to-purple-600",
  capital_investment: "from-yellow-500 to-amber-600",
};

export default function ClientServices() {
  const nav = useNavigate();
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showChoose, setShowChoose] = useState(false);

  const load = async () => {
    try {
      const [cats, all, rec] = await Promise.all([
        api.get("/services/categories"),
        api.get("/services?limit=500"),
        api.get("/services/recommended/me"),
      ]);
      setCategories(cats.data?.categories || []);
      setServices(all.data?.items || []);
      setRecommended(rec.data?.recommended_service_ids || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load services");
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = services;
    if (activeCat) list = list.filter((s) => s.category_id === activeCat);
    if (q) {
      const ql = q.toLowerCase();
      list = list.filter((s) =>
        s.name?.toLowerCase().includes(ql) ||
        s.description?.toLowerCase().includes(ql) ||
        s.category_name?.toLowerCase().includes(ql)
      );
    }
    return list;
  }, [services, q, activeCat]);

  const byId = useMemo(() => Object.fromEntries(services.map((s) => [s.id, s])), [services]);
  const recServices = useMemo(() => recommended.map((id) => byId[id]).filter(Boolean).slice(0, 6), [recommended, byId]);
  const popular = useMemo(() => services.filter((s) => s.is_popular).slice(0, 8), [services]);

  const toggle = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const removeSelected = (id) => setSelectedIds((prev) => prev.filter((x) => x !== id));
  const goCheckout = () => {
    if (selectedIds.length === 0) {
      toast.error("Pick at least one service first");
      return;
    }
    try { sessionStorage.setItem("cq_services_selected", JSON.stringify(selectedIds)); } catch { /* no-op */ }
    nav("/portal/services/checkout");
  };

  return (
    <AppShell>
      <div className="cq-in max-w-6xl">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Client portal · New Services</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-1">
              Everything You Need for Your Next Move
            </h1>
            <p className="text-slate-600 mt-2 max-w-2xl">
              Explore credit, funding, business development and marketing services personalized to your goals.
              Pick one or many — book a single consultation with your team.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              className="border-slate-200"
              onClick={() => nav("/portal/my-service-requests")}
              data-testid="services-my-requests"
            >
              <ClipboardList size={14} className="mr-1" /> My Service Requests
            </Button>
            <Button
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
              onClick={() => setShowChoose(true)}
              data-testid="services-help-choose"
            >
              <Sparkles size={14} className="mr-1" /> Help Me Choose
            </Button>
            {/* Compact cart chip — sits in the header row so the cart is
                always visible in the top-right. Number badge shows count. */}
            <button
              onClick={() => {
                if (selectedIds.length === 0) {
                  toast("Add a service first, then come back here to check out.");
                  return;
                }
                goCheckout();
              }}
              className={`relative inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-bold border transition ${
                selectedIds.length > 0
                  ? "bg-slate-900 text-white border-slate-900 hover:bg-slate-800"
                  : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
              }`}
              data-testid="services-cart-chip"
              aria-label="Cart"
            >
              <ShoppingCart size={14} />
              Cart
              {selectedIds.length > 0 && (
                <span className="ml-1 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-black">
                  {selectedIds.length}
                </span>
              )}
              {selectedIds.length > 0 && (
                <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-amber-400 animate-ping" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        {/* Search + filters */}
        <div className="mt-6 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search services (e.g. LLC, Shopify, funding, branding)…"
              className="pl-9"
              data-testid="services-search"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveCat(null)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${!activeCat ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
              data-testid="services-cat-all"
            >
              All ({services.length})
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id === activeCat ? null : c.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${activeCat === c.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
                data-testid={`services-cat-${c.id}`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>

        {/* Top cart — appears the moment they add a service so they never
            have to scroll to find the checkout. Sticks to the top of the
            content area on scroll. */}
        {selectedIds.length > 0 && (
          <div
            className="mt-4 sticky top-16 z-30 rounded-2xl border border-amber-200 bg-white/95 backdrop-blur px-4 py-3 shadow-lg shadow-amber-100"
            data-testid="services-top-cart"
          >
            <div className="flex items-center gap-3 flex-wrap">
              <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center shadow">
                <ShoppingCart size={16} />
              </div>
              <p className="text-sm font-bold text-slate-800">
                {selectedIds.length} service{selectedIds.length === 1 ? "" : "s"} in your cart
              </p>
              <div className="flex-1 min-w-0 overflow-x-auto flex gap-2">
                {selectedIds.map((id) => {
                  const s = byId[id];
                  if (!s) return null;
                  return (
                    <span key={id} className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-3 py-1 text-xs font-bold whitespace-nowrap">
                      {s.name}
                      <button onClick={() => removeSelected(id)} className="ml-1 text-amber-600 hover:text-amber-900" aria-label={`Remove ${s.name}`}>
                        <X size={12} />
                      </button>
                    </span>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  className="border-slate-200"
                  onClick={() => setSelectedIds([])}
                  data-testid="services-clear-cart"
                >
                  Clear
                </Button>
                <Button
                  onClick={goCheckout}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="services-continue-top"
                >
                  Continue <ArrowRight size={14} className="ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Recommended — desktop / tablet only (mobile sees them in All services) */}
        {recServices.length > 0 && !q && !activeCat && (
          <section className="mt-8 hidden md:block">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={16} className="text-amber-500" />
              <h2 className="font-display text-2xl font-black text-slate-900">Recommended for You</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recServices.map((s) => (
                <ServiceCard key={s.id} service={s} selected={selectedIds.includes(s.id)} onToggle={toggle} onLearn={setDetail} />
              ))}
            </div>
          </section>
        )}

        {/* Popular — desktop / tablet only */}
        {popular.length > 0 && !q && !activeCat && (
          <section className="mt-8 hidden md:block">
            <div className="flex items-center gap-2 mb-3">
              <Star size={16} className="text-amber-500 fill-amber-500" />
              <h2 className="font-display text-2xl font-black text-slate-900">Popular Services</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {popular.map((s) => (
                <ServiceCard key={s.id} service={s} compact selected={selectedIds.includes(s.id)} onToggle={toggle} onLearn={setDetail} />
              ))}
            </div>
          </section>
        )}

        {/* All / Filtered */}
        <section className="mt-8">
          <h2 className="font-display text-2xl font-black text-slate-900 mb-3">
            {activeCat ? categories.find((c) => c.id === activeCat)?.name : q ? `Results for “${q}”` : "All services"}
            <span className="ml-2 text-sm text-slate-400 font-semibold">{filtered.length}</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="services-grid">
            {filtered.map((s) => (
              <ServiceCard key={s.id} service={s} selected={selectedIds.includes(s.id)} onToggle={toggle} onLearn={setDetail} />
            ))}
          </div>
          {filtered.length === 0 && (
            <div className="text-center text-slate-400 text-sm py-16">No services match that search.</div>
          )}
        </section>

        {/* Sticky selection bar */}
        {selectedIds.length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur px-4 py-3 shadow-2xl">
            <div className="max-w-6xl mx-auto flex items-center gap-3 flex-wrap">
              <p className="text-sm font-bold text-slate-800">
                {selectedIds.length} service{selectedIds.length === 1 ? "" : "s"} selected
              </p>
              <div className="flex-1 min-w-0 overflow-x-auto flex gap-2">
                {selectedIds.map((id) => {
                  const s = byId[id];
                  if (!s) return null;
                  return (
                    <span key={id} className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-3 py-1 text-xs font-bold whitespace-nowrap">
                      {s.name}
                      <button onClick={() => removeSelected(id)} className="ml-1 text-amber-600 hover:text-amber-900" aria-label={`Remove ${s.name}`}>
                        <X size={12} />
                      </button>
                    </span>
                  );
                })}
              </div>
              <Button
                onClick={goCheckout}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="services-continue"
              >
                Continue to Consultation <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Learn More drawer */}
        {detail && (
          <ServiceDetailModal
            service={detail}
            onClose={() => setDetail(null)}
            onAdd={(s) => { if (!selectedIds.includes(s.id)) setSelectedIds((p) => [...p, s.id]); setDetail(null); }}
          />
        )}

        {/* Help Me Choose modal */}
        {showChoose && (
          <HelpMeChooseModal
            onClose={() => setShowChoose(false)}
            onRecommend={(ids) => {
              setRecommended(ids);
              setShowChoose(false);
              toast.success(`We recommended ${ids.length} services for you.`);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        )}
      </div>
    </AppShell>
  );
}

function ServiceCard({ service, selected, onToggle, onLearn, compact }) {
  const Icon = serviceIconFor(service);
  const accent = CAT_ACCENT[service.category_id] || "from-slate-800 to-slate-600";
  return (
    <Card
      className={`p-4 rounded-2xl border transition group ${selected ? "border-amber-400 bg-amber-50/60 shadow-md" : "border-slate-100 hover:border-slate-200 hover:shadow-sm"}`}
      data-testid={`service-card-${service.id}`}
    >
      <div className="flex items-start gap-3">
        <AppTile Icon={Icon} accent={accent} size="md" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest font-black text-slate-500">
            {service.category_name}
            {service.brand ? <> · <span className="text-slate-400">{service.brand}</span></> : null}
          </p>
          <h3 className="font-display font-black text-slate-900 leading-tight mt-0.5 line-clamp-2">{service.name}</h3>
        </div>
      </div>
      {!compact && (
        <p className="text-sm text-slate-600 mt-3 line-clamp-3">{service.description}</p>
      )}
      <div className="mt-3 flex items-center justify-between gap-2">
        <p className="text-[11px] text-slate-500 font-semibold">{service.consultation_type}</p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onLearn(service)}
            className="text-xs font-bold text-slate-600 hover:text-slate-900"
            data-testid={`service-learn-${service.id}`}
          >
            Learn more
          </button>
          <button
            onClick={() => onToggle(service.id)}
            className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-bold transition ${
              selected
                ? "bg-emerald-500 text-white hover:bg-emerald-600"
                : "bg-slate-900 text-white hover:bg-slate-800"
            }`}
            data-testid={`service-add-${service.id}`}
          >
            {selected ? <><Check size={12} /> Added</> : <>+ Add Service</>}
          </button>
        </div>
      </div>
    </Card>
  );
}

/** Apple-app-style rounded-square icon tile with gloss + inner ring. */
function AppTile({ Icon, accent, size = "md" }) {
  const dims = size === "lg" ? "h-14 w-14" : "h-11 w-11";
  const rad  = size === "lg" ? "rounded-[18px]" : "rounded-[14px]";
  const iconSize = size === "lg" ? 24 : 20;
  return (
    <div
      className={`${dims} ${rad} bg-gradient-to-br ${accent} relative shrink-0 flex items-center justify-center overflow-hidden shadow-md shadow-slate-900/10`}
      aria-hidden="true"
    >
      {/* Top-light gloss */}
      <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/25 to-transparent pointer-events-none" />
      {/* Inner hairline ring */}
      <span className={`absolute inset-0 ${rad} ring-1 ring-inset ring-white/20 pointer-events-none`} />
      <Icon size={iconSize} strokeWidth={2.2} className="text-white relative z-10 drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]" />
    </div>
  );
}

function ServiceDetailModal({ service, onClose, onAdd }) {
  const Icon = serviceIconFor(service);
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <Card className="max-w-lg w-full rounded-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="service-detail-modal">
        <div className="flex items-start gap-3">
          <AppTile Icon={Icon} accent={CAT_ACCENT[service.category_id] || "from-slate-800 to-slate-600"} size="lg" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest font-black text-slate-500">{service.category_name} · {service.brand}</p>
            <h3 className="font-display text-2xl font-black text-slate-900 mt-1">{service.name}</h3>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-800" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <p className="text-slate-700 mt-4 leading-relaxed">{service.description}</p>
        <div className="mt-4 rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
          <b>Consultation:</b> {service.consultation_type}
        </div>
        <p className="text-[11px] text-slate-400 mt-4">
          Consultations only — we don't guarantee credit-score changes, approvals, funding amounts,
          investment returns or legal outcomes.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button
            onClick={() => onAdd(service)}
            className="bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="service-detail-add"
          >
            <Send size={13} className="mr-1" /> Add to my consultation
          </Button>
        </div>
      </Card>
    </div>,
    document.body
  );
}

function HelpMeChooseModal({ onClose, onRecommend }) {
  const [state, setState] = useState({
    goal: "", stage: "", service_type: "", timeline: "", budget: "",
    need_funding: null, want_package: null,
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/services/help-me-choose", state);
      onRecommend(data.recommended_service_ids || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Try again");
    } finally { setBusy(false); }
  };
  const set = (k, v) => setState((prev) => ({ ...prev, [k]: v }));

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <Card className="max-w-xl w-full rounded-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="help-me-choose-modal">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest font-black text-amber-600">Help Me Choose</p>
            <h3 className="font-display text-2xl font-black text-slate-900 mt-1">Tell us what you're building</h3>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-800"><X size={18} /></button>
        </div>
        <div className="mt-4 space-y-4">
          <Field label="1. What are you trying to accomplish?">
            <Input value={state.goal} onChange={(e) => set("goal", e.target.value)} placeholder="e.g. Launch my clothing brand, get $50K in business funding" />
          </Field>
          <Field label="2. Are you starting something new or growing something existing?">
            <div className="flex gap-2">
              {[["new","Starting new"],["growing","Growing existing"]].map(([v,l]) => (
                <button key={v} onClick={() => set("stage", v)} className={`px-3 py-1.5 rounded-full text-xs font-bold border ${state.stage===v?"bg-slate-900 text-white border-slate-900":"bg-white text-slate-700 border-slate-200"}`}>{l}</button>
              ))}
            </div>
          </Field>
          <Field label="3. What type of service do you need?">
            <Input value={state.service_type} onChange={(e) => set("service_type", e.target.value)} placeholder="e.g. LLC + website + marketing" />
          </Field>
          <Field label="4. Desired timeline">
            <div className="flex flex-wrap gap-2">
              {["ASAP","30 days","60-90 days","6+ months"].map((v) => (
                <button key={v} onClick={() => set("timeline", v)} className={`px-3 py-1.5 rounded-full text-xs font-bold border ${state.timeline===v?"bg-slate-900 text-white border-slate-900":"bg-white text-slate-700 border-slate-200"}`}>{v}</button>
              ))}
            </div>
          </Field>
          <Field label="5. Approximate budget">
            <div className="flex flex-wrap gap-2">
              {["Under $1K","$1K–$5K","$5K–$15K","$15K–$50K","$50K+"].map((v) => (
                <button key={v} onClick={() => set("budget", v)} className={`px-3 py-1.5 rounded-full text-xs font-bold border ${state.budget===v?"bg-slate-900 text-white border-slate-900":"bg-white text-slate-700 border-slate-200"}`}>{v}</button>
              ))}
            </div>
          </Field>
          <Field label="6. Would you like help with funding?">
            <YesNo value={state.need_funding} onChange={(v) => set("need_funding", v)} />
          </Field>
          <Field label="7. Would you like Credit Quack to recommend a complete package?">
            <YesNo value={state.want_package} onChange={(v) => set("want_package", v)} />
          </Field>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
            data-testid="help-choose-submit"
          >
            <Sparkles size={13} className="mr-1" /> Recommend services
          </Button>
        </div>
      </Card>
    </div>,
    document.body
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <p className="text-xs font-bold text-slate-700 mb-1.5">{label}</p>
      {children}
    </label>
  );
}

function YesNo({ value, onChange }) {
  return (
    <div className="flex gap-2">
      {[[true,"Yes"],[false,"No"]].map(([v,l]) => (
        <button key={l} onClick={() => onChange(v)} className={`px-3 py-1.5 rounded-full text-xs font-bold border ${value===v?"bg-slate-900 text-white border-slate-900":"bg-white text-slate-700 border-slate-200"}`}>{l}</button>
      ))}
    </div>
  );
}
